import { EmptyState } from "@/components/EmptyState";
import { LoadingPanel } from "@/components/LoadingPanel";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDateGB } from "@/lib/format";
import { type RepairStatus, getSupabase } from "@/lib/supabase";
import { ChevronRight, Wrench } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

type ContractorRepair = {
  id: string;
  title: string;
  status: RepairStatus;
  primary_service: string | null;
  contractor_dispatched_at: string | null;
  property: { display_name: string; line1: string; line2: string | null; postcode: string } | null;
};

const STATUS_LABEL: Record<RepairStatus, string> = {
  reported: "Reported",
  triaged: "Triaged",
  contractor_assigned: "Assigned",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_PILL: Record<RepairStatus, string> = {
  reported: "bg-white/5 text-ink-dim border-line",
  triaged: "bg-blue-500/15 text-blue-200 border-blue-500/30",
  contractor_assigned: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  in_progress: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30",
  completed: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-200 border-red-500/30",
};

export function ContractorRepairs() {
  const [repairs, setRepairs] = useState<ContractorRepair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      // Resolve our contractor row first, then filter repairs by contractor_id.
      // RLS already blocks cross-contractor reads, but filtering on the wire
      // keeps the response tight and the intent explicit.
      const { data: u } = await sb.auth.getUser();
      if (!u.user) {
        if (!cancelled) {
          setRepairs([]);
          setLoading(false);
        }
        return;
      }
      const { data: me } = await sb
        .from("contractors")
        .select("id")
        .eq("user_id", u.user.id)
        .maybeSingle();
      if (!me) {
        if (!cancelled) {
          setRepairs([]);
          setLoading(false);
        }
        return;
      }
      const { data, error: err } = await sb
        .from("repairs")
        .select(
          "id, title, status, primary_service, contractor_dispatched_at, property:properties(display_name, line1, line2, postcode)",
        )
        .eq("contractor_id", me.id)
        .order("contractor_dispatched_at", { ascending: false, nullsFirst: false });
      if (cancelled) return;
      if (err) setError(err.message);
      setRepairs((data as unknown as ContractorRepair[] | null) ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <LoadingPanel />;

  const open = repairs.filter((r) => r.status !== "completed" && r.status !== "cancelled");
  const closed = repairs.filter((r) => r.status === "completed" || r.status === "cancelled");

  return (
    <div className="flex flex-col gap-6">
      <header>
        <span className="text-xs uppercase tracking-[0.18em] text-ink-dim">Your repairs</span>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mt-2">Assigned to you</h1>
        <p className="text-sm text-ink-dim mt-1">
          Tap a repair to see the brief, photos and triage report, and to upload your completion
          docs.
        </p>
      </header>

      {error ? (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      ) : null}

      {repairs.length === 0 ? (
        <EmptyState
          title="No repairs assigned yet"
          description="When Trinium dispatches a repair to you, it'll appear here."
        />
      ) : null}

      {open.length > 0 ? <RepairList title="Open" repairs={open} /> : null}
      {closed.length > 0 ? <RepairList title="Closed" repairs={closed} /> : null}
    </div>
  );
}

function RepairList({ title, repairs }: { title: string; repairs: ContractorRepair[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {repairs.length} repair{repairs.length === 1 ? "" : "s"}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y divide-line">
          {repairs.map((r) => (
            <li key={r.id}>
              <Link
                to={`/contractor/${r.id}`}
                className="w-full text-left px-6 py-4 hover:bg-white/5 transition flex items-center gap-4"
              >
                <Wrench size={18} className="text-accent shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{r.title}</span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider border ${
                        STATUS_PILL[r.status]
                      }`}
                    >
                      {STATUS_LABEL[r.status]}
                    </span>
                    {r.primary_service ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-bg-card border border-line text-ink-dim">
                        {r.primary_service}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-xs text-ink-dim mt-1">
                    {r.property?.display_name ?? "—"}
                    {r.contractor_dispatched_at
                      ? ` · Dispatched ${formatDateGB(r.contractor_dispatched_at)}`
                      : ""}
                  </p>
                </div>
                <ChevronRight size={16} className="text-ink-dim shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
